import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sprout, Truck, PhoneCall, MapPin, Navigation, ShieldCheck, Menu, X, LogIn, Package, FileText, Building2, IndianRupee, Megaphone, ImageOff } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useLang, LangToggle } from "@/lib/i18n";
import OtpBoxes from "@/components/OtpBoxes";
import QuickActions from "@/components/QuickActions";

const FALLBACK = {
  business_name: "M/S Sabuj Sona Fertilizer",
  trade_name: "M/S Sabuj Sona Fertilizer",
  owner_name: "Sonali Das Barman",
  business_type: "Proprietorship",
  category: "Wholesale Fertilizer Dealer",
  license_no: "F67939",
  udyam_no: "UDYAM-WB-05-0070832",
  gstin: "19EIPB6671Q1ZG",
  stall_no: "Stall No. 9, Mathabhanga-I Krishak Bazar",
  landmark: "Panchanan More (Sitai More), Mathabhanga",
  district: "Cooch Behar",
  state: "West Bengal",
  pin: "736146",
  country: "India",
  description: "M/S Sabuj Sona Fertilizer is a wholesale fertilizer dealer supplying agricultural fertilizers in bulk to authorized dealers and retailers.",
  contact_numbers: ["7865014730", "9933348949", "9083169718", "9083030055"],
  maps_link: "https://www.google.com/maps/search/?api=1&query=Krishak%20Bazar%2C%20Mathabhanga%2C%20Cooch%20Behar%2C%20West%20Bengal%20736146",
  logo_url: null,
  owner_photo_url: null,
  shop_photos: [],
  announcement: "",
  announcement_active: false,
};

function Logo({ name, light }) { const imageUrl = (u) => u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  const { t } = useLang();
  return (
    <div className="flex items-center gap-3">
      <div className="h-11 w-11 rounded-lg bg-[#0B192C] flex items-center justify-center shrink-0 overflow-hidden">
        {name.logo_url ? (
          <img src={imageUrl(name.logo_url)} alt="logo" className="h-full w-full object-cover" />
        ) : (
          <Sprout className="h-6 w-6 text-white" />
        )}
      </div>
      <div className="leading-tight">
        <div className={`font-display font-extrabold tracking-tight ${light ? "text-white" : "text-[#0B192C]"}`}>
          <span className="block text-sm sm:text-base">{name.business_name}</span>
        </div>
        <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 mt-0.5">{t("wholesaleTag")}</div>
      </div>
    </div>
  );
}

function PhotoBox({ url, alt, testid, className }) {
  const { t } = useLang();
  if (url) {
    return <img src={url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt={alt} data-testid={testid} className={`w-full object-cover ${className || "h-56"}`} />;
  }
  return (
    <div data-testid={`${testid}-placeholder`} className={`w-full flex flex-col items-center justify-center text-center bg-slate-100 border-2 border-dashed border-slate-300 ${className || "h-56"}`}>
      <ImageOff className="h-8 w-8 text-slate-400" />
      <div className="mt-2 px-3 text-sm font-extrabold tracking-wide text-slate-500 uppercase">{t("photoNotAvailable")}</div>
      <div className="text-xs text-slate-400 mt-1 px-3">{t("photoNotAvailableSub")}</div>
    </div>
  );
}

function LoginCard() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLang();
  const { clear } = useCart();
  const [step, setStep] = useState("mobile");
  const [role, setRole] = useState(null);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const existingSession = localStorage.getItem("sf_device_session");
    const existingRole = localStorage.getItem("sf_role");
    const existingUser = localStorage.getItem("sf_user");

    if (existingSession && existingRole === "admin" && existingUser) {
      try {
        const u = JSON.parse(existingUser);
        if (u?.mobile) {
          setMobile(u.mobile);
          setRole("admin");
          setStep("adminUnlock");
        }
      } catch {}
    }
  }, []);

  const checkMobile = async () => {
    if (!/^\d{10}$/.test(mobile.replace(/\D/g, "").slice(-10))) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/check-mobile", { mobile });
      setRole(data.role);
      if (data.role === "admin") {
        await requestOtp("admin_login");
      } else if (data.role === "customer") {
        setStep("customerChoice");
      } else {
        setStep("unregistered");
      }
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async (purpose) => {
    try {
      const { data } = await api.post("/auth/request-otp", { mobile, purpose });
      if (data.demo_otp) toast.info(`Demo mode — your OTP is ${data.demo_otp}`, { duration: 8000 });
      setOtp("");
      setStep("otp");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const unlockAdmin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin-unlock", {
        device_session: localStorage.getItem("sf_device_session"),
        pin,
      });
      login(data.token, data.user, "admin");
      clear();
      navigate("/admin");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyAdmin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin-login", { mobile, otp, pin });
      login(data.token, data.user, "admin", data.device_session);
      clear();
      navigate("/admin");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyCustomerOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { mobile, otp });
      login(data.token, data.user, "customer");
      clear();
      navigate("/customer");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const requestPasswordReset = async () => {
    setBusy(true);
    try {
      await api.post("/auth/request-otp", { mobile, purpose: "customer_password_reset" });
      setOtp("");
      setResetPassword("");
      setResetPasswordConfirm("");
      setStep("resetOtp");
      toast.success("OTP sent to your registered mobile number");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const resetCustomerPassword = async () => {
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    if (resetPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        mobile,
        otp,
        password: resetPassword,
      });
      toast.success("Password reset successfully. Please login.");
      setPassword("");
      setResetPassword("");
      setResetPasswordConfirm("");
      setOtp("");
      setStep("password");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const loginPassword = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login-password", { mobile, password });
      login(data.token, data.user, "customer");
      clear();
      navigate("/customer");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-card p-7 w-full" data-testid="login-card">
      <h2 className="font-display text-2xl font-bold text-slate-900">{t("dealerLogin")}</h2>
      {step === "mobile" && (
        <>
          <p className="text-base text-slate-600 mt-2 mb-5">{t("loginCardSub")}</p>
          <label className="sf-label !text-sm" htmlFor="login-mobile">{t("mobileNumber")}</label>
          <div className="flex gap-2">
            <span className="inline-flex items-center px-4 rounded-lg border border-slate-300 bg-slate-100 text-base font-semibold text-slate-600">+91</span>
            <input
              id="login-mobile"
              data-testid="welcome-mobile-input"
              className="sf-input !text-lg !py-3.5"
              inputMode="numeric"
              placeholder="98XXXXXXXX"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              onKeyDown={(e) => e.key === "Enter" && checkMobile()}
            />
          </div>
          <button data-testid="login-continue-btn" className="sf-btn-primary w-full !py-4 !text-base mt-5" onClick={checkMobile} disabled={busy}>
            <LogIn className="h-5 w-5" /> {busy ? "..." : t("continue")}
          </button>
          <p className="text-base text-slate-600 mt-5 text-center">
            {t("newCustomer")}{" "}
            <Link data-testid="create-account-link" to="/register" className="sf-link">
              {t("createDealerAccount")}
            </Link>
          </p>
        </>
      )}

      {step === "unregistered" && (
        <>
          <p className="text-base text-slate-600 mt-2 mb-5">{t("notRegisteredSub", { m: `+91 ${mobile}` })}</p>
          <Link to="/register" state={{ mobile }} data-testid="go-register-btn" className="sf-btn-primary w-full !py-4 !text-base">
            {t("createAccount")}
          </Link>
          <button className="sf-link text-base mt-4 mx-auto block" onClick={() => setStep("mobile")}>
            {t("useDifferentNumber")}
          </button>
        </>
      )}

      {step === "customerChoice" && (
        <>
          <p className="text-base text-slate-600 mt-2 mb-5">
            <span className="font-semibold text-slate-900">+91 {mobile}</span> — {t("howWouldYouLike")}
          </p>
          <button data-testid="choose-otp-login-btn" className="sf-btn-primary w-full !py-4 !text-base" onClick={() => requestOtp("customer_login")}>
            {t("loginWithOtp")}
          </button>
          <button data-testid="choose-password-login-btn" className="sf-btn-outline w-full !py-4 !text-base mt-3" onClick={() => setStep("password")}>
            {t("loginWithPassword")}
          </button>
          <button className="sf-link text-base mt-4 mx-auto block" onClick={() => setStep("mobile")}>
            {t("useDifferentNumber")}
          </button>
        </>
      )}

      {step === "adminUnlock" && (
      <>
        <p className="text-base text-slate-600 mt-2 mb-5">
          Admin access for <span className="font-semibold text-slate-900">+91 {mobile}</span>
        </p>

        <div className="mb-5">
          <label className="sf-label !text-sm" htmlFor="admin-unlock-pin">
            {t("adminPinLabel")}
          </label>
          <input
            id="admin-unlock-pin"
            data-testid="admin-unlock-pin-input"
            type="password"
            inputMode="numeric"
            autoFocus
            maxLength={6}
            className="sf-input !text-lg !py-3.5 tracking-[0.3em]"
            placeholder="••••••"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && pin.length === 6 && !busy) unlockAdmin();
            }}
          />
        </div>

        <button
          data-testid="admin-unlock-btn"
          className="sf-btn-primary w-full !py-4 !text-base"
          disabled={busy || pin.length !== 6}
          onClick={unlockAdmin}
        >
          {busy ? "..." : "Unlock Admin"}
        </button>

        <button
          className="sf-link text-base mt-4 mx-auto block"
          onClick={() => {
            localStorage.removeItem("sf_device_session");
            localStorage.removeItem("sf_token");
            localStorage.removeItem("sf_user");
            localStorage.removeItem("sf_role");
            setPin("");
            setMobile("");
            setRole(null);
            setStep("mobile");
          }}
        >
          Login with OTP
        </button>
      </>
    )}

    {step === "otp" && (
        <>
          <p className="text-base text-slate-600 mt-2">
            {t("otpSentTo")} <span className="font-semibold text-slate-900">+91 {mobile}</span>
            {role === "admin" ? " (Admin)" : ""}
          </p>
          <div className="my-5">
            <OtpBoxes value={otp} onChange={setOtp} testid="login-otp" />
          </div>
          {role === "admin" && (
            <div className="mb-4">
              <label className="sf-label !text-sm" htmlFor="admin-pin">{t("adminPinLabel")}</label>
              <input
                id="admin-pin"
                data-testid="admin-pin-input"
                type="password"
                inputMode="numeric"
                className="sf-input !text-lg !py-3.5 tracking-[0.3em]"
                placeholder="••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </div>
          )}
          <button
            data-testid="otp-verify-btn"
            className="sf-btn-primary w-full !py-4 !text-base"
            disabled={busy || otp.length !== 6 || (role === "admin" && pin.length !== 6)}
            onClick={role === "admin" ? verifyAdmin : verifyCustomerOtp}
          >
            {busy ? "..." : role === "admin" ? t("loginAsAdmin") : t("verifyLogin")}
          </button>
          <div className="flex justify-between mt-4 text-base">
            <button className="sf-link" onClick={() => setStep("mobile")}>
              {t("changeNumber")}
            </button>
            <button data-testid="resend-otp-btn" className="sf-link" onClick={() => requestOtp(role === "admin" ? "admin_login" : "customer_login")}>
              {t("resendOtp")}
            </button>
          </div>
        </>
      )}

      {step === "resetOtp" && (
        <>
          <p className="text-base text-slate-600 mt-2">
            Enter the OTP sent to <span className="font-semibold text-slate-900">+91 {mobile}</span>
          </p>
          <div className="my-5">
            <OtpBoxes value={otp} onChange={setOtp} testid="reset-password-otp" />
          </div>
          <button
            data-testid="reset-password-verify-btn"
            className="sf-btn-primary w-full !py-4 !text-base"
            disabled={busy || otp.length !== 6}
            onClick={() => setStep("resetPassword")}
          >
            {busy ? "..." : "Verify OTP"}
          </button>
          <div className="flex justify-between mt-4 text-base">
            <button className="sf-link" onClick={() => setStep("password")}>
              Back
            </button>
            <button className="sf-link" onClick={requestPasswordReset} disabled={busy}>
              Resend OTP
            </button>
          </div>
        </>
      )}

      {step === "resetPassword" && (
        <>
          <p className="text-base text-slate-600 mt-2 mb-5">
            Create a new password for <span className="font-semibold text-slate-900">+91 {mobile}</span>
          </p>
          <label className="sf-label !text-sm" htmlFor="reset-password">
            New Password
          </label>
          <input
            id="reset-password"
            data-testid="reset-password-input"
            type="password"
            className="sf-input !text-lg !py-3.5"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
          />
          <label className="sf-label !text-sm mt-4" htmlFor="reset-password-confirm">
            Confirm New Password
          </label>
          <input
            id="reset-password-confirm"
            data-testid="reset-password-confirm-input"
            type="password"
            className="sf-input !text-lg !py-3.5"
            value={resetPasswordConfirm}
            onChange={(e) => setResetPasswordConfirm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && resetCustomerPassword()}
          />
          <button
            data-testid="reset-password-submit-btn"
            className="sf-btn-primary w-full !py-4 !text-base mt-5"
            disabled={busy}
            onClick={resetCustomerPassword}
          >
            {busy ? "..." : "Reset Password"}
          </button>
          <button
            className="sf-link text-base mt-4 mx-auto block"
            onClick={() => setStep("resetOtp")}
            disabled={busy}
          >
            Back
          </button>
        </>
      )}

      {step === "password" && (
        <>
          <p className="text-base text-slate-600 mt-2 mb-5">+91 {mobile}</p>
          <label className="sf-label !text-sm" htmlFor="login-password">{t("password")}</label>
          <input
            id="login-password"
            data-testid="login-password-input"
            type="password"
            className="sf-input !text-lg !py-3.5"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loginPassword()}
          />
          <button data-testid="password-login-btn" className="sf-btn-primary w-full !py-4 !text-base mt-5" onClick={loginPassword} disabled={busy}>
            {busy ? "..." : t("login")}
          </button>
          <button
            data-testid="forgot-password-btn"
            className="sf-link text-base mt-4 mx-auto block"
            onClick={requestPasswordReset}
            disabled={busy}
          >
            Forgot Password?
          </button>
          <div className="flex justify-between mt-4 text-base">
            <button className="sf-link" onClick={() => setStep("mobile")}>
              {t("back")}
            </button>
            <button className="sf-link" onClick={() => requestOtp("customer_login")}>
              {t("useOtpInstead")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Welcome() {
  const { t, tAny } = useLang();
  const [biz, setBiz] = useState(FALLBACK);
  const [products, setProducts] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/public/business").then((r) => setBiz({ ...FALLBACK, ...r.data })).catch(() => {});
    api.get("/public/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : (Array.isArray(r.data.products) ? r.data.products : []))).catch(() => {});
  }, []);

  const address = biz;
  const fullAddress = `${address.stall_no}, ${address.landmark}, ${address.district}, ${address.state} – ${address.pin}, ${address.country}`;
  const shopPhotos = biz.shop_photos || [];
  const companies = [...new Set(products.map((p) => p.company_name))];
  const navLinks = [
    ["#home", t("navHome")],
    ["#business-details", t("navBusiness")],
    ["#gallery", t("storePhotos")],
    ["#products", t("navProducts")],
    ["#contact", t("navContactUs")],
  ];

  const detailRows = [
    [t("legalName"), biz.owner_name],
    [t("tradeName"), biz.trade_name],
    [t("businessType"), biz.business_type],
    [t("businessCategory"), biz.category],
    [t("licenseNo"), biz.license_no],
    [t("udyamNo"), biz.udyam_no],
    [t("gstin"), biz.gstin],
    [t("store"), biz.stall_no],
    [t("location"), biz.landmark],
    [t("district"), biz.district],
    [t("state"), biz.state],
    [t("pin"), biz.pin],
    [t("country"), biz.country],
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" id="home">
      {/* 1. HEADER */}
      <header className="bg-white border-b-4 border-[#0B192C] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          <Logo name={biz} />
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map(([href, label]) => (
              <a key={href} href={href} className="text-base font-semibold text-slate-700 hover:text-[#0B192C] transition-colors">
                {label}
              </a>
            ))}
            <a href="#dealer-login" className="sf-btn-primary !py-2.5" data-testid="header-dealer-login-btn">
              <LogIn className="h-5 w-5" /> {t("navDealerLogin")}
            </a>
            <LangToggle />
          </nav>
          <div className="flex lg:hidden items-center gap-3">
            <LangToggle />
            <button data-testid="public-mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)} className="p-2 text-[#0B192C]" aria-label="Menu">
              {menuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="public-mobile-menu">
            {navLinks.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)} className="block px-5 py-4 text-lg font-semibold text-slate-800 border-b border-slate-100">
                {label}
              </a>
            ))}
            <a href="#dealer-login" onClick={() => setMenuOpen(false)} className="block px-5 py-4 text-lg font-bold text-white bg-[#0B192C]">
              <LogIn className="inline h-5 w-5 mr-2" /> {t("navDealerLogin")}
            </a>
          </div>
        )}
      </header>

      {biz.announcement_active && biz.announcement && (
        <div className="bg-amber-50 border-b-2 border-amber-300" data-testid="announcement-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 text-amber-900">
            <Megaphone className="h-5 w-5 shrink-0" />
            <span className="text-base font-semibold">{biz.announcement}</span>
          </div>
        </div>
      )}

      {/* 2-7. HERO: NAME + LOGIN ON AN AGRICULTURAL BACKDROP */}
      <section className="relative border-b border-slate-200 overflow-hidden" data-testid="hero-section">
        <img src="https://images.unsplash.com/photo-1556490496-45afc7b8b8e5?q=80&w=2400&auto=format&fit=crop" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" data-testid="hero-bg-field" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/85 to-white/85" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="inline-flex items-center gap-1.5 text-sm font-extrabold uppercase tracking-wider text-emerald-800">
              <ShieldCheck className="h-4 w-4" /> {t("whoFor")}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0B192C] mt-4 leading-tight" data-testid="public-business-name">
              {biz.business_name}
            </h1>
            <p className="font-display text-xl sm:text-2xl font-bold text-emerald-700 uppercase tracking-wide mt-2">
              {t("wholesaleTag")}
            </p>
          </div>
          <div id="dealer-login" className="w-full">
            <LoginCard />
          </div>
        </div>
      </section>

      {/* 8. BUSINESS INTRODUCTION */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="sf-card p-7">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{t("introTitle")}</h2>
          <p className="text-lg text-slate-700 mt-4 leading-relaxed">{biz.description || t("introText")}</p>
          <p className="mt-4 text-base font-semibold text-slate-800 bg-amber-50 border-2 border-amber-200 rounded-lg p-4" data-testid="not-grocery-note">
            {t("introNotGrocery")}
          </p>
        </div>
      </section>

      {/* 8b. SHOP & WAREHOUSE GALLERY */}
      <section id="gallery" className="max-w-6xl mx-auto px-4 sm:px-6 pb-12" data-testid="gallery-section">
        <div className="flex items-center gap-3 mb-7">
          <div className="h-11 w-11 rounded-lg bg-[#0B192C] flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{t("shopWarehouse")}</h2>
            <p className="text-base text-slate-600">{t("photosSub")}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(shopPhotos.length ? shopPhotos : [null, null, null]).map((u, i) => (
            <PhotoBox key={i} url={u} alt={`Shop / warehouse ${i + 1}`} testid={`gallery-photo-${i}`} className="h-56 rounded-xl" />
          ))}
        </div>
      </section>

      {/* 10. PRODUCTS */}
      <section id="products" className="relative bg-white border-y border-slate-200 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1710141530542-f792450e736e?q=80&w=2400&auto=format&fit=crop" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.06]" data-testid="products-bg-bags" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="products-section">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-[#0B192C] flex items-center justify-center">
              <Package className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{t("ourProducts")}</h2>
              <p className="text-base text-slate-600">{t("ourProductsSub")}</p>
            </div>
          </div>
          <div className="mt-8 space-y-6">
            {companies.map((c) => (
              <div key={c} className="sf-card p-6" data-testid={`products-company-${c}`}>
                <div className="flex items-center gap-2.5 mb-4">
                  <Building2 className="h-5 w-5 text-[#1E3E62]" />
                  <h3 className="font-display text-xl font-bold text-slate-900">{c}</h3>
                </div>
                <div className="flex flex-wrap gap-3">
                  {products.filter((p) => p.company_name === c).map((p) => (
                    <div key={p.id} data-testid={`product-chip-${p.id}`} className="rounded-lg border-2 border-slate-200 px-4 py-3 min-w-44">
                      <div className="font-bold text-slate-900">{p.name}</div>
                      <div className={`text-sm font-semibold mt-0.5 ${p.available ? "text-emerald-700" : "text-rose-700"}`}>
                        {p.available ? `● ${t("availableTag")}` : `● ${t("outOfStockTag")}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {products.length === 0 && <div className="text-lg text-slate-400">{t("noCompanies")}</div>}
          </div>
        </div>
      </section>

      {/* 11-12. BUSINESS DETAILS / CREDENTIALS */}
      <section id="business-details" className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="business-details-section">
        <div className="flex items-center gap-3 mb-7">
          <div className="h-11 w-11 rounded-lg bg-[#0B192C] flex items-center justify-center">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{t("businessDetails")}</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="sf-card overflow-hidden relative">
            <table className="w-full">
              <tbody>
                {detailRows.slice(0, 7).map(([k, v], i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50/70" : ""}>
                    <td className="sf-td !py-3.5 text-slate-500 font-bold uppercase tracking-wider text-xs sm:text-sm w-1/2">{k}</td>
                    <td className="sf-td !py-3.5 font-semibold text-slate-900 text-base" data-testid={`bp-row-${i}`}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sf-card overflow-hidden relative">
            <table className="w-full">
              <tbody>
                {detailRows.slice(7).map(([k, v], i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50/70" : ""}>
                    <td className="sf-td !py-3.5 text-slate-500 font-bold uppercase tracking-wider text-xs sm:text-sm w-1/2">{k}</td>
                    <td className="sf-td !py-3.5 font-semibold text-slate-900 text-base" data-testid={`bp-row-${i + 7}`}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 13. CONTACT NUMBERS */}
      <section id="contact" className="relative bg-[#0B192C] text-white overflow-hidden" data-testid="contact-section">
        <img src="https://images.unsplash.com/photo-1651678938439-fc3f8f03eda5?q=80&w=2400&auto=format&fit=crop" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.1]" data-testid="contact-bg-field" />
        <div className="absolute inset-0 bg-[#0B192C]/70" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3">
            <PhoneCall className="h-8 w-8 text-emerald-400" />
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold">{t("contactTitle")}</h2>
          </div>
          <p className="text-lg text-slate-300 mt-2">{t("contactSub")}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {(biz.contact_numbers || []).map((n, i) => (
              <a key={n} href={`tel:+91${n}`} data-testid={`contact-call-${i}`} className="flex items-center justify-between rounded-xl bg-white/5 border-2 border-white/10 px-6 py-5 hover:bg-white/10 transition-colors">
                <span className="flex items-center gap-4">
                  <span className="h-12 w-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <PhoneCall className="h-6 w-6 text-emerald-400" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">+91</span>
                    <span className="font-mono-sf text-2xl font-bold">{n}</span>
                  </span>
                </span>
                <span className="rounded-lg bg-emerald-500 px-4 py-2 text-base font-extrabold text-white uppercase">{t("callNow")}</span>
              </a>
            ))}
          </div>
        {(biz.whatsapp_link || biz.facebook_link) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {biz.whatsapp_link && (
              <a href={biz.whatsapp_link} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="flex items-center justify-center gap-2 rounded-xl bg-green-500 hover:bg-green-600 px-5 py-4 text-base font-extrabold text-white transition-colors shadow-sm">
                <span className="text-xl">WhatsApp</span>
              </a>
            )}
            {biz.facebook_link && (
              <a href={biz.facebook_link} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-btn" className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-4 text-base font-extrabold text-white transition-colors shadow-sm">
                <span className="text-xl">Facebook</span>
              </a>
            )}
          </div>
        )}
          <div className="mt-8 rounded-xl bg-white/5 border-2 border-white/10 p-5 flex items-start gap-3" data-testid="contact-address">
            <MapPin className="h-6 w-6 text-emerald-400 shrink-0 mt-1" />
            <div className="text-lg font-semibold leading-relaxed">
              {fullAddress}
            </div>
          </div>
          <a href={biz.maps_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 font-extrabold text-white hover:bg-emerald-600 transition-colors">
            <Navigation className="h-5 w-5" /> {t("getDirections")}
          </a>
        </div>
      </section>

      {/* 14. GOOGLE MAPS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="maps-section">
        <div className="flex items-center gap-2.5 mb-6">
          <MapPin className="h-6 w-6 text-[#1E3E62]" />
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{t("mapTitle")}</h2>
        </div>
        <div className="sf-card overflow-hidden relative" data-testid="maps-card">
          <iframe
            title="Shop location map"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(`${biz.stall_no} ${biz.landmark} ${biz.district} ${biz.state} ${biz.pin}`)}&output=embed`}
            className="w-full h-80 border-0 pointer-events-none"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="maps-embed"
        />
        <a href={biz.maps_link} target="_blank" rel="noopener noreferrer" aria-label="Open shop location in Google Maps" className="absolute inset-0 z-10" />
        </div>
      </section>

      {/* 15. ABOUT */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3">
            <IndianRupee className="h-7 w-7 text-[#1E3E62]" />
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{t("aboutTitle")}</h2>
          </div>
          <div className="mt-6 grid lg:grid-cols-3 gap-5 items-start">
            <p className="lg:col-span-2 text-lg text-slate-700 leading-relaxed">{t("aboutText")}</p>
            <div className="sf-card p-6 text-center" data-testid="proprietor-card">
              <h3 className="font-display text-lg font-bold text-slate-900">{t("ourProprietor")}</h3>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-0.5">{biz.owner_name}</div>
              <div className="mt-4 max-w-64 mx-auto">
                <PhotoBox url={biz.owner_photo_url} alt="Owner" testid="owner-photo" className="h-72 rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 16. FOOTER */}
      <footer className="bg-[#0B192C] text-slate-300 border-t-4 border-emerald-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-3 gap-8">
          <div>
            <Logo name={biz} light />
            <p className="text-sm text-slate-400 mt-3">{t("footerNote")}</p>
          </div>
          <div className="text-sm">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t("storeAddress")}</div>
            <div className="leading-relaxed text-slate-300" data-testid="footer-address">
              {address.stall_no}<br />
              {address.landmark}<br />
              {address.district}, {address.state} – {address.pin}<br />
              {address.country}
            </div>
          </div>
          <div className="text-sm">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t("contactTitle")}</div>
            {(biz.contact_numbers || []).map((n) => (
              <a key={n} href={`tel:+91${n}`} className="block font-mono-sf text-base font-semibold text-slate-200 hover:text-white py-0.5" data-testid={`footer-call-${n}`}>
                +91 {n}
              </a>
            ))}
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} {biz.business_name} · {t("wholesaleTag")}</span>
            <span className="font-mono-sf">F.L. {biz.license_no} · GSTIN {biz.gstin}</span>
          </div>
        </div>
      </footer>
      <QuickActions biz={biz} />
    </div>
  );
}
